import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toastOn } from "../../../../../store/slices/toast";
import { ClientUploadImageApi } from "../../../../../http/httpRequest";

export default function ClientNegocioFotoModalCreate({
  userId,
  open,
  onClose,
  onSuccess,
}) {
  const dispatch = useDispatch();

  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [file, setFile] = useState(null);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setButtonDisabled(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [open]);

  if (!open) return null;

  const resetFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const f = e?.target?.files?.[0];
    if (!f) return;

    if (!f.type?.startsWith("image/")) {
      dispatch(toastOn({ type: "danger", message: "Solo se permiten imágenes." }));
      resetFile();
      return;
    }

    if (f.size > 5000000) {
      dispatch(toastOn({ type: "danger", message: "El límite por imagen es de 5 MB." }));
      resetFile();
      return;
    }

    setFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      dispatch(toastOn({ type: "warning", message: "Selecciona una imagen primero." }));
      return;
    }

    try {
      setButtonDisabled(true);

      // 1) Subir a Cloudinary
      const form = new FormData();
      form.append("file", file);
      form.append("upload_preset", "jbq6smvh");

      const cloudRes = await fetch(
        "https://api.cloudinary.com/v1_1/ddapdj8v2/image/upload",
        { method: "POST", body: form }
      );

      const cloudJson = await cloudRes.json();

      if (cloudRes.status !== 200 || !cloudJson?.secure_url) {
        dispatch(toastOn({ type: "danger", message: "Error al subir imagen (Cloudinary)." }));
        setButtonDisabled(false);
        return;
      }

      // 2) Guardar URL en tu backend
      const payload = { user_id: userId, foto_negocio: cloudJson.secure_url };
      const saveRes = await ClientUploadImageApi(payload);

      if (saveRes.status === 200 || saveRes.status === 201) {
        dispatch(toastOn({ type: "success", message: "Imagen guardada correctamente!" }));
        setButtonDisabled(false);
        resetFile();
        onClose?.();
        onSuccess?.();
      } else {
        dispatch(toastOn({ type: "warning", message: "La imagen podría no haberse guardado correctamente." }));
        setButtonDisabled(false);
      }
    } catch (err) {
      dispatch(toastOn({ type: "danger", message: "Error al guardar imagen." }));
      setButtonDisabled(false);
    }
  };

  const onBackdropClick = (e) => {
    if (e.target?.dataset?.backdrop === "true" && !buttonDisabled) onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-3"
      data-backdrop="true"
      onMouseDown={onBackdropClick}
    >
      <div className="w-full max-w-2xl rounded-lg bg-white dark:bg-gray-800 shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-600 p-4">
          <h2 className="text-base font-semibold text-gray-700 dark:text-gray-100">
            Subir foto del negocio
          </h2>

          <button
            type="button"
            onClick={() => !buttonDisabled && onClose?.()}
            className="rounded px-2 py-1 text-gray-600 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
            title="Cerrar"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="p-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              Selecciona una imagen (máx 5 MB)
            </label>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="mt-2 block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 p-2 text-sm text-gray-700 dark:text-gray-200"
              onChange={handleFileChange}
              disabled={buttonDisabled}
            />

            {file && (
              <div className="mt-3 flex items-center justify-between rounded border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 p-2">
                <div className="text-sm text-gray-700 dark:text-gray-200">
                  <div>
                    <strong>Archivo:</strong> {file.name}
                  </div>
                  <div className="text-gray-500">
                    Tamaño: {Math.round(file.size / 1024)} KB
                  </div>
                </div>

                <button
                  type="button"
                  onClick={resetFile}
                  disabled={buttonDisabled}
                  className="rounded border border-red-500 px-3 py-1 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  Quitar
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 border-t border-gray-200 dark:border-gray-600 p-4">
            <button
              type="button"
              onClick={() => !buttonDisabled && onClose?.()}
              disabled={buttonDisabled}
              className="rounded px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100 disabled:opacity-50"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={buttonDisabled || !file}
              className="rounded px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
            >
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
